// ANN-3: dimension anchors resolve against CURRENT geometry on every use — that's what
// makes dimensions associative. INV-6 conversion helpers live here too.
import type { Vec } from "../core/geometry";
import type { DimAnchor, DimensionEntity, Doc } from "./entities";

/** Resolve an anchor to a world point, or null when the reference is broken. */
export function resolveAnchor(doc: Doc, anchor: DimAnchor): Vec | null {
  if (anchor.kind === "free") return anchor.p;
  const entity = doc.entities[anchor.entityId];
  if (!entity) return null;
  switch (entity.type) {
    case "polyline": {
      if (anchor.anchor === "vertex" || anchor.anchor === "start" || anchor.anchor === "end") {
        const i =
          anchor.anchor === "start" ? 0 : anchor.anchor === "end" ? entity.points.length - 1 : (anchor.index ?? 0);
        return entity.points[i] ?? null; // EC-ANN-4: out-of-range → broken
      }
      return null;
    }
    case "circle":
      return anchor.anchor === "center" ? entity.center : null;
    case "arc": {
      if (anchor.anchor === "center") return entity.center;
      const deg =
        anchor.anchor === "start" ? entity.startDeg : anchor.anchor === "end" ? entity.startDeg + entity.sweepDeg : null;
      if (deg === null) return null;
      const rad = (deg * Math.PI) / 180;
      return { x: entity.center.x + entity.r * Math.cos(rad), y: entity.center.y + entity.r * Math.sin(rad) };
    }
    default:
      return null;
  }
}

export function dimensionEndpoints(doc: Doc, dim: DimensionEntity): { a: Vec; b: Vec } | null {
  const a = resolveAnchor(doc, dim.a);
  const b = resolveAnchor(doc, dim.b);
  return a && b ? { a, b } : null;
}

/** Is either anchor an entity reference into `ids`? (INV-6 delete cascade.) */
export function dimensionReferences(dim: DimensionEntity, ids: ReadonlySet<string>): boolean {
  return (
    (dim.a.kind === "entity" && ids.has(dim.a.entityId)) ||
    (dim.b.kind === "entity" && ids.has(dim.b.entityId))
  );
}

/** INV-6: freeze referencing anchors at their last resolved position + orphan flag. */
export function orphanPatches(
  doc: Doc,
  deletedIds: ReadonlySet<string>
): { id: string; before: Record<string, unknown>; after: Record<string, unknown> }[] {
  const patches: { id: string; before: Record<string, unknown>; after: Record<string, unknown> }[] = [];
  for (const entity of Object.values(doc.entities)) {
    if (entity.type !== "dimension" || deletedIds.has(entity.id)) continue;
    if (!dimensionReferences(entity, deletedIds)) continue;
    const freeze = (anchor: DimAnchor): DimAnchor => {
      if (anchor.kind === "entity" && deletedIds.has(anchor.entityId)) {
        const p = resolveAnchor(doc, anchor) ?? { x: 0, y: 0 };
        return { kind: "free", p: { ...p } };
      }
      return structuredClone(anchor);
    };
    patches.push({
      id: entity.id,
      before: { a: structuredClone(entity.a), b: structuredClone(entity.b), meta: entity.meta },
      after: { a: freeze(entity.a), b: freeze(entity.b), meta: { ...entity.meta, orphan: true } }
    });
  }
  return patches;
}
